import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useNavigate } from '@tanstack/react-router'
import { optimizedAdminService, ChallengeWithTranslations } from '../services/optimizedAdminService'
import { adminService, Challenge } from '../services/adminService'
import { Button } from '../components/ui/Button'

import { ChallengeList } from '../components/ChallengeList'
import { ChallengeTranslationModal } from '../components/ChallengeTranslationModal'
import { ChallengeBulkTranslationModal } from '../components/ChallengeBulkTranslationModal'
import { BulkTranslationProgressModal } from '../components/BulkTranslationProgressModal'
import { DeepSeekSetupNotice } from '../components/DeepSeekSetupNotice'
// import { useLanguages } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { seedSampleData } from '../utils/seedDatabase'
import { translationService } from '../services/translationService'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'

interface TranslationProgress {
  language: string
  status: 'pending' | 'translating' | 'completed' | 'error' | 'saving'
  startTime?: number
  endTime?: number
  error?: string
  translatedText?: string
}

export function ChallengesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showTranslations, setShowTranslations] = useState<string | null>(null)
  const [showBulkTranslation, setShowBulkTranslation] = useState(false)
  const [showBulkProgressModal, setShowBulkProgressModal] = useState(false)
  const [bulkTranslationProgress, setBulkTranslationProgress] = useState<Record<string, TranslationProgress>>({})
  const [isSeeding, setIsSeeding] = useState(false)
  const [togglingChallengeId, setTogglingChallengeId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ['challenges'],
    queryFn: () => adminService.getChallenges(),
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => adminService.getCategories(),
  })

  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => adminService.updateChallenge(id, { 
      status: challenges.find(c => c.id === id)?.status === 'active' ? 'inactive' : 'active' 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.notifications.statusUpdated'))
      setTogglingChallengeId(null)
    },
    onError: () => {
      toast.error(t('challenges.notifications.statusUpdateFailed'))
      setTogglingChallengeId(null)
    },
  })


  const handleTranslations = (challengeId: string) => {
    setShowTranslations(challengeId)
  }

  const handleToggleStatus = (challenge: Challenge) => {
    setTogglingChallengeId(challenge.id)
    toggleStatusMutation.mutate(challenge.id)
  }

  const handleBulkTranslation = async (sourceLanguage: string, targetLanguages: string[], challengeIds: string[], maxConcurrency: number = 3) => {
    setShowBulkTranslation(false)
    
    // Initialize progress tracking
    const progress: Record<string, TranslationProgress> = {}
    challengeIds.forEach(challengeId => {
      targetLanguages.forEach(lang => {
        const key = `${challengeId}-${lang}`
        progress[key] = {
          language: lang,
          status: 'pending'
        }
      })
    })
    setBulkTranslationProgress(progress)
    setShowBulkProgressModal(true)

    // Configure translation service
    translationService.setConfig({
      temperature: 1.3,
      useJsonMode: true
    })

    // Process bulk translations in parallel
    const processTranslations = async () => {
      // Prepare all translation requests
      const allRequests: Array<{
        request: any,
        challengeId: string,
        targetLang: string,
        challenge: any,
        key: string
      }> = []

      for (const challengeId of challengeIds) {
        const challenge = challenges.find(c => c.id === challengeId)
        if (!challenge) continue

        // Get existing challenge translations to find source content
        const existingTranslations = await adminService.getChallengeTranslations(challengeId)
        let sourceContent = existingTranslations.find(t => t.language === sourceLanguage)

        // If no translation exists for source language, try to use challenge's default name/description
        if (!sourceContent && sourceLanguage === 'en' && challenge.name) {
          sourceContent = {
            id: '',
            challengeId: challengeId,
            language: 'en',
            name: challenge.name,
            description: challenge.description || ''
          }
        }

        if (!sourceContent?.name) {
          // Mark all target languages for this challenge as error
          targetLanguages.forEach(lang => {
            const key = `${challengeId}-${lang}`
            setBulkTranslationProgress(prev => ({
              ...prev,
              [key]: { 
                ...prev[key], 
                status: 'error', 
                error: `No ${sourceLanguage} translation found for challenge ${challenge.name || challengeId}`
              }
            }))
          })
          continue
        }

        for (const targetLang of targetLanguages) {
          const key = `${challengeId}-${targetLang}`
          allRequests.push({
            request: {
              sourceLanguage,
              targetLanguage: targetLang,
              text: {
                name: sourceContent.name,
                description: sourceContent.description || ''
              },
              temperature: 1.3,
              useJsonMode: true,
              type: 'challenge' // Use challenge type for challenges
            },
            challengeId,
            targetLang,
            challenge,
            key
          })
        }
      }

      // Mark all translations as starting
      allRequests.forEach(({ key }) => {
        setBulkTranslationProgress(prev => ({
          ...prev,
          [key]: { ...prev[key], status: 'translating', startTime: Date.now() }
        }))
      })

      try {
        // Process translations in parallel with progress tracking
        await translationService.translateParallel(
          allRequests.map(item => item.request),
          async (completed, _, result, error, requestIndex) => {
            // Use the correct requestIndex instead of completed - 1
            const { challengeId, targetLang, challenge, key } = allRequests[requestIndex!]

            if (error) {
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'error', 
                  endTime: Date.now(),
                  error: error instanceof Error ? error.message : 'Translation failed'
                }
              }))

              const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
              toast.error(`Failed to translate ${challenge.name} to ${languageName}: ${error instanceof Error ? error.message : 'Translation failed'}`)
            } else if (result) {
              // Update progress to completed
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'completed',
                  endTime: Date.now(),
                  translatedText: result.name
                }
              }))

              // Update progress to saving
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'saving'
                }
              }))

              // Auto-save translation to database (check for existing first)
              try {
                // First check if translation already exists
                const translations = await adminService.getChallengeTranslations(challengeId)
                const existing = translations.find(t => t.language === targetLang)
                
                if (existing) {
                  // Update existing translation
                  await adminService.updateChallengeTranslation(existing.id, {
                    name: result.name,
                    description: result.description || ''
                  })
                  const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
                  console.log(`✅ Updated existing challenge translation: ${challenge.name} -> ${languageName}`, {
                    challengeId,
                    targetLanguage: targetLang,
                    translatedName: result.name,
                    key: key
                  })
                } else {
                  // Create new translation
                  await adminService.createChallengeTranslation({
                    challengeId,
                    language: targetLang,
                    name: result.name,
                    description: result.description || ''
                  })
                  const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
                  console.log(`✅ Created new challenge translation: ${challenge.name} -> ${languageName}`, {
                    challengeId,
                    targetLanguage: targetLang,
                    translatedName: result.name,
                    key: key
                  })
                }

                // Update progress to saved
                setBulkTranslationProgress(prev => ({
                  ...prev,
                  [key]: { 
                    ...prev[key], 
                    status: 'completed'
                  }
                }))

              } catch (dbError) {
                console.error('Failed to save challenge translation to database:', dbError)
                setBulkTranslationProgress(prev => ({
                  ...prev,
                  [key]: { 
                    ...prev[key], 
                    status: 'error',
                    error: 'Failed to save to database'
                  }
                }))
              }

              const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
              // Check if this is a fallback translation (contains language indicator)
              const isFallback = result.name.includes(`(${languageName})`)
              const translationType = isFallback ? '⚠️ Fallback' : '✅ AI Translated'
              console.log(`${translationType}: ${challenge.name} → ${languageName} (${result.name})`)
            }
          },
          maxConcurrency // Use configured concurrency for bulk operations
        )

        // All translations completed - show final status
        const finalProgress = Object.values(bulkTranslationProgress)
        const savedCount = finalProgress.filter(p => p.status === 'completed').length
        const errorCount = finalProgress.filter(p => p.status === 'error').length
        const totalRequested = allRequests.length
        
        if (savedCount > 0) {
          toast.success(`Successfully translated and saved ${savedCount}/${totalRequested} challenge translations`)
        }
        if (errorCount > 0) {
          toast.error(`Failed to process ${errorCount}/${totalRequested} challenge translations`)
        }
        
        queryClient.invalidateQueries({ queryKey: ['challenges'] })
        queryClient.invalidateQueries({ queryKey: ['challengeTranslations'] })
        
      } catch (error) {
        console.error('Bulk challenge translation failed:', error)
        toast.error('Bulk challenge translation process failed')
      }
    }

    // Start the translation process
    processTranslations()
  }

  const handleSeedDatabase = async () => {
    setIsSeeding(true)
    try {
      await seedSampleData()
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('Sample data seeded successfully!')
    } catch (error) {
      toast.error('Failed to seed sample data')
    } finally {
      setIsSeeding(false)
    }
  }



  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <DeepSeekSetupNotice />
      
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
              {t('challenges.title')}
            </h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
              {t('challenges.subtitle')}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowBulkTranslation(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {t('actions.bulkTranslate')}
            </button>
            <button
              onClick={() => navigate({ to: '/challenges/create' })}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:border-blue-700 transition-all duration-200 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('challenges.createChallenge')}
            </button>
          </div>
        </div>
      </div>



      {challenges.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('challenges.noChallengesFound')}
            </h3>
            <p className="text-gray-500 mb-6">
              {t('challenges.createFirstChallenge')}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate({ to: '/challenges/create' })}
                variant="primary"
                className="w-full"
              >
                {t('challenges.createChallenge')}
              </Button>
              <Button
                onClick={handleSeedDatabase}
                variant="secondary"
                loading={isSeeding}
                className="w-full"
              >
                Seed Sample Data
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <ChallengeList
          challenges={challenges}
          categories={categories}
          onTranslations={handleTranslations}
          onToggleStatus={handleToggleStatus}
          togglingChallengeId={togglingChallengeId}
        />
      )}

      {showTranslations && (
        <ChallengeTranslationModal
          challengeId={showTranslations}
          onClose={() => setShowTranslations(null)}
        />
      )}

      {/* Bulk Translation Modal */}
      {showBulkTranslation && (
        <ChallengeBulkTranslationModal
          challenges={challenges}
          onTranslate={handleBulkTranslation}
          onClose={() => setShowBulkTranslation(false)}
        />
      )}

      {/* Bulk Translation Progress Modal */}
      {showBulkProgressModal && (
        <BulkTranslationProgressModal
          progress={bulkTranslationProgress}
          onClose={() => setShowBulkProgressModal(false)}
        />
      )}
    </div>
  )
}